import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, input, OnInit } from '@angular/core';
import { DatePipe } from '@angular/common';
import { FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';

// translation service
import { TranslatePipe } from '@ngx-translate/core';

import { Months } from '../../../../models/common';
import { IClientDocumentGroups } from '../../../../models/client';
import { ClientService } from '../../../../services/client-service';
import { DragDropUploadComponent } from '../../../../utils/drag-drop-upload-component/drag-drop-upload-component';

import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatCardModule } from '@angular/material/card';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';


@Component( {
  selector: 'app-documents-component',
  imports: [
    FormsModule,
    ReactiveFormsModule,
    DatePipe,
    TranslatePipe,
    MatCardModule,
    MatIconModule,
    MatButtonModule,
    MatSelectModule,
    MatInputModule,
    FormsModule,
    DragDropUploadComponent,
  ],
  templateUrl: './documents-component.html',
  styleUrl: './documents-component.css',
  changeDetection: ChangeDetectionStrategy.OnPush
} )
export class DocumentsComponent implements OnInit, AfterViewInit
{
  constructor(
    private _clientService: ClientService,
    private _fb: FormBuilder,
    private _cdr: ChangeDetectorRef,
  ) { }

  clientId = input.required<string>();
  documents: IClientDocumentGroups | undefined;
  formGroup: FormGroup | undefined;

  months: string[] = Months;
  years: number[] = [];
  yearsSpan = 10;
  files: File[] = [];


  async ngOnInit(): Promise<void>
  {
    this.formGroup = await this.buildFormGroup();

    this.documents = await this.getData();

    this._cdr.detectChanges();

  }

  ngAfterViewInit(): void
  {
    this._cdr.detectChanges();
  }

  private async getData(): Promise<IClientDocumentGroups | undefined>
  {
    // get documents
    const docs = await this._clientService.getDocuments( this.clientId() );
    const groupDocs = await this._clientService.getGroupedDocuments( docs?.clientDocs || [] );

    return groupDocs;
  }

  private async buildFormGroup(): Promise<FormGroup>
  {
    const selectedMonth = new Date().getMonth();
    const selectedYear = new Date().getFullYear();
    const selectedFile: string = '';

    // set years from current year going back x years
    for ( let year = selectedYear; year >= ( selectedYear - this.yearsSpan ); --year )
    {
      this.years.push( year );
    }
    //  create group
    const group: { [key: string]: FormControl } = {};
    group['selectedMonth'] = new FormControl( selectedMonth );
    group['selectedYear'] = new FormControl( selectedYear );
    group['selectedFile'] = new FormControl( selectedFile, Validators.required );
    return this._fb.group( group );
  }

  private async resetForm()
  {
    this.files = [];
    if ( this.formGroup )
    {
      this.formGroup.reset( { selectedMonth: new Date().getMonth(), selectedYear: new Date().getFullYear(), selectedFile: '' } );
    }    
  }

  public async upload()
  {
    if ( this.files.length > 0 )
    {
      await this._clientService.uploadDocuments(
        this.clientId(),
        this.formGroup?.get( 'selectedMonth' )!.value,
        this.formGroup?.get( 'selectedYear' )!.value,
        this.formGroup?.get( 'selectedFile' )!.value,
        this.files, undefined );

      // wait half second before getting data back
      setTimeout( async () =>
      {
        this.documents = await this.getData();
        this._cdr.detectChanges(); 
      }, 500)
      
      await this.resetForm();     
    }

    this._cdr.detectChanges();    
  }

  public async remove( documentId: number )
  {
    await this._clientService.deleteDocument( this.clientId(), documentId );

    this.documents = await this.getData();
    this._cdr.detectChanges();
  }

  public async download( documentId: number ): Promise<void>
  {   
    const response = await this._clientService.downloadDocument( documentId );
    if ( response )
    {     
      window.open( response, '_blank' );
    }
  }

}
